import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
    Background, Controls, MiniMap, Handle, Position,
    useNodesState, useEdgesState, ReactFlowProvider, useReactFlow, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Box, Flex, Text, Heading, Switch, Input, Button, IconButton, useToast,
    VStack, HStack, Badge, Divider, Select, Textarea, SimpleGrid, Tooltip,
    Alert, AlertIcon, AlertTitle, AlertDescription, Spinner, Tag, TagLabel, Wrap, WrapItem,
    NumberInput, NumberInputField, FormControl, FormLabel, FormHelperText,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
    Tabs, TabList, Tab, TabPanels, TabPanel, useDisclosure, useBreakpointValue,
    Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import {
    FaPhoneAlt, FaPlay, FaStop, FaPlus, FaTrash, FaSave, FaVolumeUp, FaSyncAlt,
    FaUserTie, FaHeadset, FaRobot, FaSignOutAlt, FaProjectDiagram,
    FaSlidersH, FaCheckCircle, FaUpload, FaHistory,
    FaListUl, FaCodeBranch, FaFlag, FaUndo, FaUnlink, FaPhoneSlash,
    FaChartBar, FaPhoneVolume,
} from 'react-icons/fa';
import Preloader from '../components/Preloader';
import { semBloqueados } from '../hooks/useUsuarios';
import PainelTab from '../components/Ura/PainelTab';
import HistoricoTab from '../components/Ura/HistoricoTab';
import {
    getUra, updateUra, salvarFluxoUra, validarFluxoUra, publicarFluxoUra,
    novoRascunhoUra, versoesUra, republicarUra, gerarAudiosUra, baixarAudioUra,
} from '../services/api';

const ACCENT = '#F68633';
const LINHA = '#A0AEC0';

const DIAS_SEMANA = [
    { v: 1, label: 'Seg' }, { v: 2, label: 'Ter' }, { v: 3, label: 'Qua' },
    { v: 4, label: 'Qui' }, { v: 5, label: 'Sex' }, { v: 6, label: 'Sáb' },
    { v: 0, label: 'Dom' },
];

/** Como cada peça aparece no desenho. */
const VISUAL = {
    audio: { icone: FaVolumeUp, cor: 'orange', rotulo: 'Tocar áudio' },
    menu: { icone: FaListUl, cor: 'orange', rotulo: 'Menu' },
    ramais: { icone: FaHeadset, cor: 'green', rotulo: 'Vendedores' },
    ramal: { icone: FaPhoneAlt, cor: 'green', rotulo: 'Ramal' },
    gerente: { icone: FaUserTie, cor: 'purple', rotulo: 'Gerente de contas' },
    bot: { icone: FaRobot, cor: 'teal', rotulo: 'Atendente virtual' },
    condicao: { icone: FaCodeBranch, cor: 'blue', rotulo: 'Condição' },
    encerrar: { icone: FaSignOutAlt, cor: 'gray', rotulo: 'Encerrar' },
    transbordo: { icone: FaPhoneSlash, cor: 'red', rotulo: 'Devolver p/ operadora' },
};

/** O nome de cada saída na boca do povo. */
const ROTULO_SAIDA = {
    fim: 'depois',
    ninguem: 'ninguém atendeu',
    sem_gerente: 'não tem gerente',
    sem_resposta: 'não digitou',
    invalido: 'opção errada',
    sim: 'sim',
    nao: 'não',
    transferir: 'pediu vendedor',
};

const rotuloSaida = (evento) =>
    evento.startsWith('digito_') ? `digitou ${evento.slice(7)}` : (ROTULO_SAIDA[evento] || evento);

/** Saídas que sempre aparecem na caixa; no menu os dígitos são escolhidos por você. */
const SAIDAS_FIXAS = {
    audio: ['fim'],
    menu: ['sem_resposta', 'invalido'],
    ramais: ['ninguem'],
    ramal: ['ninguem'],
    gerente: ['ninguem', 'sem_gerente'],
    bot: ['ninguem', 'transferir'],
    condicao: ['sim', 'nao'],
    encerrar: [],
    transbordo: [],
};

// Saídas que podem ficar sem destino: "pediu vendedor" vazio só quer dizer que
// esse atendente virtual não passa a ligação para ninguém.
const SAIDAS_OPCIONAIS = {
    bot: ['transferir'],
};
const saidaOpcional = (tipo, evento) => (SAIDAS_OPCIONAIS[tipo] || []).includes(evento);

const saidasDoNo = (no) => {
    const fixas = SAIDAS_FIXAS[no.tipo] || [];
    const extras = Object.keys(no.saidas || {}).filter((e) => !fixas.includes(e));
    extras.sort();
    return [...extras, ...fixas];
};

const gradeDefault = () =>
    [0, 1, 2, 3, 4, 5, 6].map((d) => ({ ativo: d >= 1 && d <= 5, inicio: '08:00', fim: '18:00' }));

const normalizarGrade = (g) => {
    const base = gradeDefault();
    if (Array.isArray(g)) {
        g.forEach((d, i) => {
            if (i < 7 && d && typeof d === 'object') {
                base[i] = { ativo: !!d.ativo, inicio: d.inicio || '08:00', fim: d.fim || '18:00' };
            }
        });
    }
    return base;
};

const novoId = (tipo) => `${tipo.slice(0, 4)}_${Math.random().toString(36).slice(2, 8)}`;

/** Por que o atendente virtual está parado, em português de gente. */
const MOTIVO_BOT = {
    desligado: 'desligado',
    fora_do_horario: 'fora do horário dele',
    teto_diario: 'teto de custo do dia',
};

/**
 * Assinatura do desenho, para saber se o que está na tela ainda é o que foi
 * salvo. Ordena as chaves porque o servidor devolve o grafo normalizado, e
 * ordem diferente não é mudança.
 */
const chaveGrafo = (g) => {
    const ordenar = (v) => {
        if (Array.isArray(v)) return v.map(ordenar);
        if (v && typeof v === 'object') {
            return Object.keys(v).sort().reduce((acc, k) => { acc[k] = ordenar(v[k]); return acc; }, {});
        }
        return v;
    };
    return JSON.stringify(ordenar(g || {}));
};

/**
 * Os campos de Ajustes que esta tela edita. O resto do que o servidor manda
 * junto (dentro_do_horario, por exemplo) é leitura, não é edição de ninguém —
 * e não pode fazer a tela achar que tem coisa não salva.
 */
const CAMPOS_CFG = ['ativo', 'whatsapp', 'email_compras', 'horario_restrito', 'horario_dias', 'textos', 'tts_voz', 'tts_velocidade', 'tts_instrucoes'];

const chaveCfg = (c) => chaveGrafo(CAMPOS_CFG.reduce((acc, k) => { acc[k] = (c || {})[k] ?? null; return acc; }, {}));

/** Resumo de uma linha, para a caixa não precisar ser aberta para ser entendida. */
const resumoDoNo = (no, ref) => {
    const p = no.params || {};
    switch (no.tipo) {
        case 'audio':
        case 'encerrar':
            return (p.texto || '').trim() ? `"${(p.texto || '').slice(0, 60)}…"` : 'sem áudio';
        case 'menu':
            return `espera ${p.espera_seg || 6}s · repete ${p.tentativas || 2}x`;
        case 'ramais':
            return `toca ${p.ring_seg || 12}s em quem estiver conectado`;
        case 'ramal': {
            const u = (ref.usuarios || []).find((x) => String(x.id) === String(p.user_id));
            return u ? `${u.nome} (${u.ramal}) · ${p.ring_seg || 12}s` : 'escolha o ramal';
        }
        case 'gerente':
            return `toca ${p.ring_seg || 20}s no ramal do gerente`;
        case 'bot': {
            const m = (ref.motivos || []).find((x) => x.valor === p.motivo);
            return m ? m.label : 'vendedor';
        }
        case 'condicao': {
            const c = (ref.condicoes || []).find((x) => x.valor === p.condicao);
            return c ? c.label : 'escolha a condição';
        }
        case 'transbordo':
            return 'sem atender: a operadora assume';
        default:
            return '';
    }
};

/* ==================================================================
 * Desenho: grafo -> reactflow
 * ================================================================== */

/** Arrumação automática por camadas, para quem nunca arrastou nada. */
const arrumar = (grafo) => {
    const nos = grafo.nos || {};
    const pos = {};
    const nivel = {};
    const fila = grafo.inicio ? [[grafo.inicio, 0]] : [];
    const vistos = {};

    while (fila.length) {
        const [id, n] = fila.shift();
        if (!nos[id] || vistos[id]) continue;
        vistos[id] = true;
        nivel[n] = nivel[n] || [];
        nivel[n].push(id);
        Object.values(nos[id].saidas || {}).forEach((d) => {
            if (d && nos[d] && !vistos[d]) fila.push([d, n + 1]);
        });
    }

    // Quem ficou fora do caminho vai para uma faixa própria, à direita.
    const orfaos = Object.keys(nos).filter((id) => !vistos[id]);
    let direita = 0;
    Object.entries(nivel).forEach(([n, ids]) => {
        const largura = (ids.length - 1) * 300;
        ids.forEach((id, i) => {
            pos[id] = { x: i * 300 - largura / 2, y: Number(n) * 170 };
            direita = Math.max(direita, pos[id].x);
        });
    });

    // À direita do que já foi desenhado: x fixo caía em cima da árvore assim
    // que um nível ficava largo, e o "Arrumar" salvava caixa sobre caixa.
    orfaos.forEach((id, i) => { pos[id] = { x: direita + 340, y: i * 170 }; });

    return pos;
};

const montarDiagrama = (grafo, ref, toqueGrande = false) => {
    const nos = grafo.nos || {};
    const auto = arrumar(grafo);
    const nodes = [];
    const edges = [];

    Object.entries(nos).forEach(([id, no]) => {
        const p = no.pos && (no.pos.x || no.pos.y) ? no.pos : (auto[id] || { x: 0, y: 0 });
        nodes.push({
            id,
            type: 'peca',
            position: { x: Number(p.x) || 0, y: Number(p.y) || 0 },
            data: {
                tipo: no.tipo,
                nome: no.nome || (VISUAL[no.tipo] || {}).rotulo || no.tipo,
                resumo: resumoDoNo(no, ref),
                saidas: saidasDoNo(no),
                ligadas: no.saidas || {},
                opcionais: SAIDAS_OPCIONAIS[no.tipo] || [],
                inicio: grafo.inicio === id,
                toqueGrande,
            },
        });

        Object.entries(no.saidas || {}).forEach(([evento, destino]) => {
            if (!destino || !nos[destino]) return;
            edges.push({
                id: `${id}::${evento}`,
                source: id,
                sourceHandle: evento,
                target: destino,
                label: rotuloSaida(evento),
                type: 'smoothstep',
                markerEnd: { type: MarkerType.ArrowClosed, color: LINHA },
                style: { stroke: LINHA, strokeWidth: 2 },
                // A faixa de clique da seta é medida em unidades do GRAFO: com
                // o zoom que o fitView escolhe no celular (~0,3) os 20 padrão
                // viram 6 px na tela — no dedo, nada.
                interactionWidth: toqueGrande ? 70 : 30,
                labelStyle: { fontSize: 11, fill: '#4A5568' },
                labelBgStyle: { fill: '#F7FAFC' },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 4,
            });
        });
    });

    return { nodes, edges };
};

/* ==================================================================
 * A caixa
 * ================================================================== */

const Peca = ({ data, selected }) => {
    const v = VISUAL[data.tipo] || { icone: FaVolumeUp, cor: 'gray' };
    const saidas = data.saidas || [];

    // No celular a tomada de 9 px cai para ~2,5 px na tela, no zoom que o
    // fitView escolhe: ninguém liga duas caixas assim. O alvo do dedo cresce
    // para FORA da caixa e é transparente; o ponto visível fica onde sempre
    // esteve. Assim arrastar a caixa pelo título continua funcionando e a
    // caixa não vira um muro de bolas.
    const toque = !!data.toqueGrande;
    const ALVO = 28;
    const PONTO = toque ? 13 : 9;

    return (
        <Box
            bg="white" border="2px solid"
            borderColor={selected ? ACCENT : `${v.cor}.200`}
            borderRadius="lg" boxShadow={selected ? '0 0 0 3px rgba(246,134,51,0.25)' : 'sm'}
            px={3} pt={2} pb={saidas.length ? (toque ? 6 : 5) : 2} w="230px" position="relative"
            _hover={{ boxShadow: 'md' }}
        >
            <Handle
                type="target" position={Position.Top}
                style={toque
                    ? { background: 'transparent', border: 'none', borderRadius: 0, width: ALVO, height: ALVO, top: -ALVO }
                    : { background: LINHA, width: 9, height: 9 }}
            >
                {toque && (
                    <Box
                        position="absolute" left="50%" bottom={`-${PONTO / 2}px`} transform="translateX(-50%)"
                        w={`${PONTO}px`} h={`${PONTO}px`} borderRadius="full" bg={LINHA}
                        border="1px solid white" pointerEvents="none"
                    />
                )}
            </Handle>

            {data.inicio && (
                <Badge
                    position="absolute" top="-10px" left="8px" colorScheme="orange"
                    borderRadius="full" fontSize="9px" px={2}
                >
                    começo
                </Badge>
            )}

            <HStack spacing={2} align="center">
                <Box as={v.icone} color={`${v.cor}.500`} boxSize="13px" flexShrink={0} />
                <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={1}>{data.nome}</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={2}>{data.resumo}</Text>

            {/* Uma "tomada" por saída: é daqui que sai a seta para a próxima caixa. */}
            {saidas.map((evento, i) => {
                const esquerda = ((i + 1) / (saidas.length + 1)) * 100;
                const ligada = !!data.ligadas[evento];
                // Opcional sem destino é escolha, não pendência: cinza, não vermelho.
                const opcional = (data.opcionais || []).includes(evento);
                const cor = ligada ? LINHA : (opcional ? '#CBD5E0' : '#FC8181');
                return (
                    <Box key={evento} position="absolute" bottom="2px" left={`${esquerda}%`} transform="translateX(-50%)">
                        <Text fontSize="8px" color={ligada || opcional ? 'gray.500' : 'red.400'} whiteSpace="nowrap" textAlign="center">
                            {rotuloSaida(evento)}
                        </Text>
                        <Handle
                            type="source" id={evento} position={Position.Bottom}
                            style={toque
                                ? { background: 'transparent', border: 'none', borderRadius: 0, width: ALVO, height: ALVO, bottom: -ALVO, left: '50%' }
                                : { background: cor, width: 9, height: 9, bottom: -6, left: '50%' }}
                        >
                            {toque && (
                                <Box
                                    position="absolute" left="50%" top={`-${PONTO / 2}px`} transform="translateX(-50%)"
                                    w={`${PONTO}px`} h={`${PONTO}px`} borderRadius="full" bg={cor}
                                    border="1px solid white" pointerEvents="none"
                                />
                            )}
                        </Handle>
                    </Box>
                );
            })}
        </Box>
    );
};

const TIPOS_NO = { peca: Peca };

/* ==================================================================
 * Peças de tela
 * ================================================================== */

const LinhaAudio = ({ audio, tocando, onOuvir, onParar }) => {
    if (!audio) return <Tag size="sm" colorScheme="gray" borderRadius="full"><TagLabel>sem áudio</TagLabel></Tag>;
    const estaTocando = tocando === audio.nome;
    return (
        <HStack spacing={2}>
            <Tag size="sm" colorScheme={audio.existe ? 'green' : 'orange'} borderRadius="full">
                <TagLabel>{audio.existe ? `${audio.duracao_seg ?? '?'}s` : 'falta gerar'}</TagLabel>
            </Tag>
            {audio.existe && (
                <Button size="xs" variant="ghost" colorScheme="orange"
                    leftIcon={estaTocando ? <FaStop /> : <FaPlay />}
                    onClick={() => (estaTocando ? onParar() : onOuvir(audio.nome))}>
                    {estaTocando ? 'parar' : 'ouvir'}
                </Button>
            )}
        </HStack>
    );
};

const Bloco = ({ titulo, descricao, children, acao }) => (
    <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: 4, md: 5 }}>
        <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap" mb={descricao ? 1 : 3}>
            <Heading as="h2" size="sm" color="gray.700">{titulo}</Heading>
            {acao}
        </Flex>
        {descricao && <Text fontSize="xs" color="gray.500" mb={4}>{descricao}</Text>}
        {children}
    </Box>
);

/* ==================================================================
 * A tela
 * ================================================================== */

const UraInterno = () => {
    const toast = useToast();
    const drawer = useDisclosure();
    const historico = useDisclosure();
    const { onOpen: abrirDrawer, onClose: fecharDrawer } = drawer;
    // ssr: false — sem isto o primeiro render assume "celular" (é o default do
    // Chakra) e o MiniMap pisca no desktop.
    const ehMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });
    // Alvo de dedo: o "xs" do Chakra tem 24 px de altura, metade do que um
    // toque acerta. No celular as ações principais sobem para 40 px.
    const alvo = ehMobile ? { size: 'sm', h: '40px', minW: '40px' } : { size: 'xs' };
    const { screenToFlowPosition } = useReactFlow();

    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [publicando, setPublicando] = useState(false);
    const [gerando, setGerando] = useState(false);

    const [cfg, setCfg] = useState({
        ativo: false, whatsapp: '', email_compras: '', horario_restrito: true,
        horario_dias: gradeDefault(), textos: {}, tts_voz: 'onyx', tts_velocidade: 1.12, tts_instrucoes: '',
    });
    const [grafo, setGrafo] = useState({ inicio: '', nos: {} });
    const [publicado, setPublicado] = useState(null);
    const [rascunhoInfo, setRascunhoInfo] = useState({});
    const [validacao, setValidacao] = useState({ erros: [], avisos: [] });
    const [audios, setAudios] = useState([]);
    const [status, setStatus] = useState({});
    const [ref, setRef] = useState({ tipos: [], condicoes: [], motivos: [], usuarios: [] });
    const [versoes, setVersoes] = useState([]);
    const [noAberto, setNoAberto] = useState(null);
    const [arestaSelecionada, setArestaSelecionada] = useState(null);
    const [tocando, setTocando] = useState(null);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null);

    // Carregamento que falhou não pode cair no editor vazio: vazio é igual a
    // "fluxo em branco" e um Salvar rascunho em cima disso apaga o desenho do
    // servidor — rascunho não tem versão para voltar.
    const [erroCarga, setErroCarga] = useState(null);
    const [carregouUmaVez, setCarregouUmaVez] = useState(false);

    // Duas pessoas desenhando ao mesmo tempo: o servidor recusa (409) quando o
    // rascunho dele é mais novo que o carimbo que a tela mandou. Aqui a recusa
    // vira escolha — e o desenho de ninguém é jogado fora em silêncio.
    const [conflito, setConflito] = useState(null);

    // A aba é controlada porque o Painel joga para as Ligações: clicar numa
    // caixa do mapa tem que abrir a lista já filtrada por ela.
    const [aba, setAba] = useState(0);
    const [pulo, setPulo] = useState(null);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // O que o servidor tem guardado, de cada lado. Serve para dizer, sem
    // enganar ninguém, se a tela já foi salva — e para o `aplicar` saber o que
    // pode sobrescrever. Em ref (o `aplicar` precisa ser estável) e em estado
    // (as contas de "não salvo" precisam re-render).
    const salvoRef = useRef(null);
    const cfgSalvoRef = useRef(null);
    const [chaveSalva, setChaveSalva] = useState(null);
    const [chaveCfgSalva, setChaveCfgSalva] = useState(null);

    // Espelho do que está na tela agora. O `aplicar` não pode depender de
    // `grafo`/`cfg`: mudaria de identidade a cada tecla, o `carregar` mudaria
    // junto e a tela recarregaria em laço.
    const grafoRef = useRef(grafo);
    const cfgRef = useRef(cfg);
    useEffect(() => { grafoRef.current = grafo; }, [grafo]);
    useEffect(() => { cfgRef.current = cfg; }, [cfg]);

    /**
     * Traz para a tela o que o servidor mandou.
     *
     * Cada lado (desenho e ajustes) é preservado se tiver mudança não salva:
     * ligar a URA e clicar em "aplicar" não pode apagar o desenho em
     * andamento, e salvar o desenho não pode apagar o que foi digitado em
     * Ajustes. Quando a resposta é a do que acabou de ser salvo, aquele lado
     * vem forçado — aí quem manda é o servidor.
     */
    const aplicar = useCallback((data, opcoes = {}) => {
        const { forcarGrafo = false, forcarCfg = false } = opcoes;

        const c = data.config || {};
        const cfgDoServidor = {
            ...cfgRef.current, ...c,
            horario_dias: normalizarGrade(c.horario_dias),
            textos: c.textos || {},
            tts_velocidade: Number(c.tts_velocidade ?? 1.12),
        };
        const cfgSujo = cfgSalvoRef.current !== null && chaveCfg(cfgRef.current) !== cfgSalvoRef.current;
        if (forcarCfg || !cfgSujo) {
            cfgRef.current = cfgDoServidor;
            cfgSalvoRef.current = chaveCfg(cfgDoServidor);
            setCfg(cfgDoServidor);
            setChaveCfgSalva(cfgSalvoRef.current);
        }

        const doServidor = data.rascunho?.grafo && data.rascunho.grafo.nos ? data.rascunho.grafo : { inicio: '', nos: {} };
        const grafoSujo = salvoRef.current !== null && chaveGrafo(grafoRef.current) !== salvoRef.current;
        if (forcarGrafo || !grafoSujo) {
            grafoRef.current = doServidor;
            salvoRef.current = chaveGrafo(doServidor);
            setGrafo(doServidor);
            setChaveSalva(salvoRef.current);
        }

        setRascunhoInfo(data.rascunho || {});
        setPublicado(data.publicado || null);
        setValidacao(data.validacao || { erros: [], avisos: [] });
        setAudios(data.audios || []);
        setStatus(data.status || {});
        if (data.ref) setRef(data.ref);
    }, []);

    const carregar = useCallback(async (opcoes = {}) => {
        try {
            const { data } = await getUra();
            aplicar(data.data || {}, opcoes);
            setErroCarga(null);
            setCarregouUmaVez(true);
        } catch (e) {
            setErroCarga(e?.response?.data?.message || 'Não consegui falar com o servidor.');
            toast({ title: 'Não consegui carregar a URA.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [aplicar, toast]);

    useEffect(() => { carregar(); }, [carregar]);

    // Trocar de áudio, parar ou sair da tela tem que soltar o blob: só o
    // `ended`/`error` deixava o arquivo inteiro vivo na memória (é o padrão que
    // a aba Ligações já usa).
    const soltarAudio = useCallback(() => {
        if (audioRef.current) {
            try { audioRef.current.pause(); } catch (_) { /* já parou */ }
            audioRef.current = null;
        }
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
    }, []);

    useEffect(() => soltarAudio, [soltarAudio]);

    /* ---------------- grafo ---------------- */

    const mudarNo = (id, patch) => setGrafo((g) => ({
        ...g,
        nos: { ...g.nos, [id]: { ...g.nos[id], ...patch } },
    }));

    const mudarParam = (id, campo, valor) => setGrafo((g) => ({
        ...g,
        nos: { ...g.nos, [id]: { ...g.nos[id], params: { ...(g.nos[id].params || {}), [campo]: valor } } },
    }));

    const mudarSaida = (id, evento, destino) => setGrafo((g) => ({
        ...g,
        nos: { ...g.nos, [id]: { ...g.nos[id], saidas: { ...(g.nos[id].saidas || {}), [evento]: destino } } },
    }));

    const removerSaida = (id, evento) => setGrafo((g) => {
        const saidas = { ...(g.nos[id]?.saidas || {}) };
        // Dígito some de vez; saída fixa só perde o destino (a tomada continua lá).
        if (evento.startsWith('digito_')) delete saidas[evento];
        else saidas[evento] = '';
        return { ...g, nos: { ...g.nos, [id]: { ...g.nos[id], saidas } } };
    });

    const removerNo = (id) => {
        const g = grafoRef.current;
        const alvoNo = g.nos?.[id];
        if (!alvoNo) return;

        const chegam = Object.entries(g.nos).filter(([outro, n]) =>
            outro !== id && Object.values(n.saidas || {}).some((d) => d === id)).length;
        const saem = Object.values(alvoNo.saidas || {}).filter(Boolean).length;
        const ehInicio = g.inicio === id;

        let aviso = `Remover a caixa "${alvoNo.nome || (VISUAL[alvoNo.tipo] || {}).rotulo || alvoNo.tipo}"?`;
        if (chegam + saem > 0) aviso += ` ${chegam + saem} ligação(ões) serão desfeitas.`;
        // Promover outra caixa a começo em silêncio (era a ordem do objeto que
        // decidia) trocava o início da URA sem ninguém pedir. O fluxo fica sem
        // começo e a validação cobra — que é o que o servidor também faz.
        if (ehInicio) aviso += ' Esta é a caixa de começo: o fluxo fica SEM começo e não dá para publicar até você marcar outra.';

        if (!window.confirm(aviso)) return;

        setGrafo((atual) => {
            const nos = { ...atual.nos };
            delete nos[id];
            Object.keys(nos).forEach((outro) => {
                const saidas = { ...(nos[outro].saidas || {}) };
                Object.keys(saidas).forEach((e) => { if (saidas[e] === id) saidas[e] = ''; });
                nos[outro] = { ...nos[outro], saidas };
            });
            return { inicio: atual.inicio === id ? '' : atual.inicio, nos };
        });
        setNoAberto(null);
        fecharDrawer();
        if (ehInicio) {
            toast({ title: 'O fluxo ficou sem começo.', description: 'Abra a caixa que começa a ligação e clique em "Começar por aqui".', status: 'warning', duration: 8000, isClosable: true });
        }
    };

    const adicionarPeca = useCallback((tipo) => {
        const id = novoId(tipo);
        const centro = (() => {
            try {
                return screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            } catch (e) {
                return { x: 0, y: 0 };
            }
        })();

        const params = {
            audio: { texto: '' },
            encerrar: { texto: '' },
            menu: { texto: '', texto_curto: '', espera_seg: 6, tentativas: 2 },
            ramais: { ring_seg: 12 },
            ramal: { user_id: null, ring_seg: 12 },
            gerente: { ring_seg: 20 },
            bot: { motivo: 'vendas' },
            condicao: { condicao: 'horario' },
            transbordo: {},
        }[tipo] || {};

        const saidas = {};
        (SAIDAS_FIXAS[tipo] || []).forEach((e) => { saidas[e] = ''; });

        setGrafo((g) => ({
            inicio: g.inicio || id,
            nos: {
                ...g.nos,
                [id]: {
                    tipo,
                    nome: (VISUAL[tipo] || {}).rotulo || tipo,
                    params,
                    saidas,
                    pos: { x: Math.round(centro.x), y: Math.round(centro.y) },
                },
            },
        }));

        setNoAberto(id);
        abrirDrawer();
    }, [abrirDrawer, screenToFlowPosition]);

    // O desenho vem do grafo. Refs estáveis para o que o reactflow chama de volta.
    const diagrama = useMemo(() => montarDiagrama(grafo, ref, ehMobile), [grafo, ref, ehMobile]);

    // O que muda o DESENHO: caixa, tomada, seta, posição — e o tamanho do alvo
    // de toque. Digitar no Drawer não entra aqui: muda só o resumo da caixa, e
    // remontar tudo a cada tecla derruba a seleção da seta (o botão "Remover
    // ligação" some sozinho) e refaz a conta de posições de todas as caixas.
    const chaveEstrutura = useMemo(() => chaveGrafo({
        m: !!ehMobile,
        i: grafo.inicio,
        n: Object.fromEntries(Object.entries(grafo.nos || {}).map(([id, n]) => [id, { t: n.tipo, s: n.saidas || {}, p: n.pos || null }])),
    }), [grafo, ehMobile]);

    const estruturaRef = useRef(null);

    useEffect(() => {
        if (estruturaRef.current === chaveEstrutura) {
            setNodes((atuais) => atuais.map((n) => {
                const novo = diagrama.nodes.find((x) => x.id === n.id);
                if (!novo || (novo.data.nome === n.data.nome && novo.data.resumo === n.data.resumo)) return n;
                return { ...n, data: { ...n.data, nome: novo.data.nome, resumo: novo.data.resumo } };
            }));
            return;
        }
        estruturaRef.current = chaveEstrutura;
        // Remontar recria os objetos e o reactflow zera a seleção junto; a seta
        // marcada tem que continuar marcada depois de mexer no grafo.
        setNodes((atuais) => {
            const marcados = new Set(atuais.filter((n) => n.selected).map((n) => n.id));
            return diagrama.nodes.map((n) => (marcados.has(n.id) ? { ...n, selected: true } : n));
        });
        setEdges((atuais) => {
            const marcadas = new Set(atuais.filter((e) => e.selected).map((e) => e.id));
            return diagrama.edges.map((e) => (marcadas.has(e.id) ? { ...e, selected: true } : e));
        });
    }, [diagrama, chaveEstrutura, setNodes, setEdges]);

    const aoConectar = useCallback((c) => {
        if (!c.source || !c.target || !c.sourceHandle) return;
        setGrafo((g) => ({
            ...g,
            nos: { ...g.nos, [c.source]: { ...g.nos[c.source], saidas: { ...(g.nos[c.source].saidas || {}), [c.sourceHandle]: c.target } } },
        }));
    }, []);

    const aoArrastar = useCallback((_e, no) => {
        if (!no?.id) return;
        setGrafo((g) => (g.nos[no.id]
            ? { ...g, nos: { ...g.nos, [no.id]: { ...g.nos[no.id], pos: { x: Math.round(no.position.x), y: Math.round(no.position.y) } } } }
            : g));
    }, []);

    const abrirNo = useCallback((_e, no) => {
        setNoAberto(no.id);
        abrirDrawer();
    }, [abrirDrawer]);

    const aoSelecionar = useCallback(({ edges: selecionadas }) => {
        setArestaSelecionada(selecionadas && selecionadas.length === 1 ? selecionadas[0] : null);
    }, []);

    const removerLigacao = () => {
        if (!arestaSelecionada) return;
        const [id, evento] = arestaSelecionada.id.split('::');
        removerSaida(id, evento);
        setArestaSelecionada(null);
    };

    const reorganizar = () => {
        setGrafo((g) => {
            const pos = arrumar(g);
            const nos = {};
            Object.entries(g.nos).forEach(([id, no]) => { nos[id] = { ...no, pos: pos[id] || { x: 0, y: 0 } }; });
            return { ...g, nos };
        });
        toast({ title: 'Diagrama arrumado. Salve o rascunho para manter assim.', status: 'info', duration: 3000 });
    };

    /* ---------------- ações ---------------- */

    const salvar = async (opcoes = {}) => {
        const { ignorarRevisao = false } = opcoes;
        setSalvando(true);
        try {
            // O carimbo que veio no carregamento volta aqui: é ele que separa
            // "salvei o meu desenho" de "apaguei o de quem salvou no meio".
            const { data } = await salvarFluxoUra({ grafo, revisao: ignorarRevisao ? '' : (rascunhoInfo.revisao || '') });
            aplicar(data.data || {}, { forcarGrafo: true });
            setConflito(null);
            toast({ title: 'Rascunho salvo.', description: 'Ainda não é o que atende: publique quando estiver pronto.', status: 'success', duration: 4000 });
        } catch (e) {
            const r = e?.response;
            if (r?.status === 409) {
                setConflito({ mensagem: r.data?.message || 'Alguém salvou o rascunho enquanto você desenhava.' });
                toast({ title: 'Não salvei: alguém mexeu no rascunho.', description: 'Escolha o que fazer no aviso acima do desenho.', status: 'warning', duration: 8000, isClosable: true });
            } else {
                toast({ title: r?.data?.message || 'Não consegui salvar o rascunho.', status: 'error', duration: 6000 });
            }
        } finally {
            setSalvando(false);
        }
    };

    const salvarConfig = async () => {
        setSalvando(true);
        try {
            const { data } = await updateUra(cfg);
            // Só os ajustes acabaram de virar verdade no servidor; o desenho da
            // tela é o que a pessoa está fazendo e não pode ser sobrescrito.
            aplicar(data.data || {}, { forcarCfg: true });
            toast({ title: 'Ajustes salvos.', status: 'success', duration: 3000 });
        } catch (e) {
            const r = e?.response?.data;
            toast({
                title: r?.message || 'Não consegui salvar.',
                description: r?.errors ? Object.values(r.errors).flat().slice(0, 2).join(' · ') : null,
                status: 'error', duration: 7000,
            });
        } finally {
            setSalvando(false);
        }
    };

    const validar = async () => {
        try {
            const { data } = await validarFluxoUra({ grafo });
            setValidacao(data.data);
            const { erros, avisos } = data.data;
            toast({
                title: erros.length ? `${erros.length} pendência(s) para resolver` : 'Fluxo inteiro, dá para publicar.',
                description: !erros.length && avisos.length ? `${avisos.length} aviso(s) — vale conferir.` : null,
                status: erros.length ? 'warning' : 'success', duration: 5000,
            });
        } catch (e) {
            toast({ title: 'Não consegui validar.', status: 'error', duration: 4000 });
        }
    };

    const publicar = async () => {
        // É o único botão que muda o que o telefone da empresa faz. Os outros
        // dois destrutivos ("Começar de…" e "voltar para esta versão") já
        // perguntam; este não perguntava.
        if (!window.confirm('Publicar este desenho? Ele passa a atender as ligações da empresa já na próxima chamada.')) return;

        setPublicando(true);
        try {
            // O que vai pro ar é o rascunho do servidor. Sem salvar antes, o
            // Publicar mandaria o desenho anterior e a recarga apagaria o que
            // está na tela — trabalho perdido sem ninguém avisar.
            const salvo = await salvarFluxoUra({ grafo, revisao: rascunhoInfo.revisao || '' });
            aplicar(salvo.data?.data || {}, { forcarGrafo: true });
            setConflito(null);

            const { data } = await publicarFluxoUra();
            aplicar(data.data || {}, { forcarGrafo: true });
            toast({
                title: `Publicado (v${data.data?.publicacao?.versao ?? '?'}).`,
                description: 'Vale na próxima ligação.',
                status: 'success', duration: 6000, isClosable: true,
            });
        } catch (e) {
            if (e?.response?.status === 409) {
                setConflito({ mensagem: e.response.data?.message || 'Alguém salvou o rascunho enquanto você desenhava.' });
                toast({ title: 'Não publiquei: alguém mexeu no rascunho.', description: 'Escolha o que fazer no aviso acima do desenho.', status: 'warning', duration: 8000, isClosable: true });
                return;
            }
            const r = e?.response?.data;
            if (r?.data?.erros || r?.data?.avisos) setValidacao(r.data);
            toast({
                title: r?.message || 'Não consegui publicar.',
                description: r?.data?.erros?.[0] || null,
                status: 'error', duration: 8000, isClosable: true,
            });
        } finally {
            setPublicando(false);
        }
    };

    const comecarDe = async (base) => {
        const rotulo = {
            publicado: 'uma cópia do que está no ar',
            branco: 'um fluxo em branco',
            antigo: 'o fluxo padrão da TAGA',
        }[base] || base;

        if (!window.confirm(`Trocar o rascunho por ${rotulo}? O desenho de agora se perde. (O que está no ar não muda.)`)) return;

        try {
            const { data } = await novoRascunhoUra({ base });
            aplicar(data.data || {}, { forcarGrafo: true, forcarCfg: true });
            setConflito(null);
            toast({ title: 'Rascunho novo na tela.', status: 'info', duration: 3000 });
        } catch (e) {
            toast({ title: 'Não consegui criar o rascunho.', status: 'error', duration: 4000 });
        }
    };

    const abrirHistorico = async () => {
        historico.onOpen();
        try {
            const { data } = await versoesUra();
            setVersoes(data.data || []);
        } catch (e) {
            setVersoes([]);
        }
    };

    const voltarPara = async (id, versao) => {
        if (!window.confirm(`Voltar para a v${versao}? Ela passa a atender as ligações agora, e o rascunho vira esse desenho.`)) return;

        try {
            const { data } = await republicarUra(id);
            aplicar(data.data || {}, { forcarGrafo: true, forcarCfg: true });
            setConflito(null);
            historico.onClose();
            toast({ title: 'Versão republicada.', status: 'success', duration: 5000 });
        } catch (e) {
            toast({ title: e?.response?.data?.message || 'Não consegui republicar.', status: 'error', duration: 6000 });
        }
    };

    const gerarAudios = async (forcar = false) => {
        setGerando(true);
        try {
            const { data } = await gerarAudiosUra({ forcar });
            setAudios(data.data?.audios || []);
            toast({
                title: 'Áudios gerados.',
                description: forcar ? 'Todos foram refeitos com a voz de agora.' : 'Só os que faltavam.',
                status: 'success', duration: 4000,
            });
        } catch (e) {
            toast({ title: 'Falha ao gerar os áudios.', description: e?.response?.data?.message, status: 'error', duration: 8000 });
        } finally {
            setGerando(false);
        }
    };

    const audioDoTexto = (texto) => {
        const t = (texto || '').trim();
        if (!t) return null;
        // O arquivo se chama pelo hash do texto que o locutor leu (com o
        // WhatsApp e o e-mail já no lugar dos marcadores); a caixa mostra o
        // texto como foi escrito. Por isso procura nos dois.
        return audios.find((a) => a.texto === t || (a.textos_brutos || []).includes(t)) || null;
    };

    const faltamAudios = useMemo(() => audios.filter((a) => !a.existe).length, [audios]);
    const naoSalvo = useMemo(() => chaveSalva !== null && chaveGrafo(grafo) !== chaveSalva, [grafo, chaveSalva]);
    const cfgNaoSalvo = useMemo(() => chaveCfgSalva !== null && chaveCfg(cfg) !== chaveCfgSalva, [cfg, chaveCfgSalva]);
    const temPendencia = naoSalvo || cfgNaoSalvo;

    // Sair da tela com desenho não salvo perdia tudo, sem uma palavra. O F5 e o
    // fechar da aba são o beforeunload (mesmo padrão do softphone em chamada);
    // o clique no menu não passa por ele — é navegação do react-router — então
    // o link é interceptado antes de o roteador ver o clique.
    useEffect(() => {
        if (!temPendencia) return undefined;

        const recado = 'A URA tem mudanças que ainda não foram salvas.';

        const aoSair = (e) => {
            e.preventDefault();
            e.returnValue = recado;
            return e.returnValue;
        };

        const aoClicar = (ev) => {
            if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
            const link = ev.target?.closest?.('a[href]');
            if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
            let destino;
            try { destino = new URL(link.href, window.location.origin); } catch (_) { return; }
            // Link para fora do sistema já é coberto pelo beforeunload.
            if (destino.origin !== window.location.origin) return;
            if (destino.pathname === window.location.pathname) return;
            if (window.confirm(`${recado}\n\nSair mesmo assim? O que não foi salvo se perde.`)) return;
            ev.preventDefault();
            ev.stopPropagation();
        };

        window.addEventListener('beforeunload', aoSair);
        document.addEventListener('click', aoClicar, true);
        return () => {
            window.removeEventListener('beforeunload', aoSair);
            document.removeEventListener('click', aoClicar, true);
        };
    }, [temPendencia]);

    const ouvir = async (nome) => {
        try {
            soltarAudio();
            const { data } = await baixarAudioUra(nome);
            const url = URL.createObjectURL(data);
            const el = new Audio(url);
            audioRef.current = el;
            audioUrlRef.current = url;
            el.onended = () => { setTocando(null); soltarAudio(); };
            el.onerror = () => { setTocando(null); soltarAudio(); };
            await el.play();
            setTocando(nome);
        } catch (e) {
            setTocando(null);
            soltarAudio();
            toast({ title: 'Não consegui tocar esse áudio.', status: 'error', duration: 4000 });
        }
    };

    const parar = () => {
        soltarAudio();
        setTocando(null);
    };

    if (loading) return <Preloader />;

    // Sem um carregamento que deu certo não existe editor: o que apareceria é
    // um fluxo em branco idêntico ao de verdade, e um Salvar rascunho em cima
    // disso apagaria o desenho do servidor (rascunho não tem versão anterior).
    if (!carregouUmaVez) {
        return (
            <Box ml={{ base: 0, md: '64px' }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
                <Box maxW="560px" mx="auto" mt={10} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: 5, md: 6 }} textAlign="center">
                    <Box as={FaPhoneAlt} boxSize="26px" color={ACCENT} mx="auto" mb={3} />
                    <Heading as="h1" size="md" color="gray.700" mb={2}>Não consegui abrir a URA</Heading>
                    <Text fontSize="sm" color="gray.600" mb={2}>{erroCarga || 'Não consegui falar com o servidor.'}</Text>
                    <Text fontSize="xs" color="gray.500" mb={5}>
                        O fluxo que está no ar continua atendendo normalmente. A tela não abre em
                        branco de propósito: salvar por cima de um editor vazio apagaria o rascunho.
                    </Text>
                    <Button colorScheme="orange" leftIcon={<FaSyncAlt />} onClick={() => { setLoading(true); carregar(); }}>
                        Tentar de novo
                    </Button>
                </Box>
            </Box>
        );
    }

    /* ---------------- editor da caixa ---------------- */

    const no = noAberto ? grafo.nos[noAberto] : null;
    // Toda caixa menos ela mesma: é o que cada saída pode escolher como destino.
    const destinos = Object.entries(grafo.nos || {}).filter(([id]) => id !== noAberto);

    const campoTexto = (campo, titulo, ajuda, linhas = 3) => (
        <FormControl>
            <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={1}>
                <FormLabel fontSize="sm" mb={0}>{titulo}</FormLabel>
                <LinhaAudio audio={audioDoTexto(no.params?.[campo])} tocando={tocando} onOuvir={ouvir} onParar={parar} />
            </Flex>
            <Textarea
                size="sm" bg="white" rows={linhas}
                value={no.params?.[campo] || ''}
                onChange={(e) => mudarParam(noAberto, campo, e.target.value)}
            />
            {ajuda && <FormHelperText fontSize="xs">{ajuda}</FormHelperText>}
        </FormControl>
    );

    const campoNumero = (campo, titulo, ajuda, min, max, padrao) => (
        <FormControl>
            <FormLabel fontSize="sm">{titulo}</FormLabel>
            {/* Apagar o conteúdo para digitar outro número devolvia o padrão na
                hora — no celular era impossível trocar o valor. O campo pode
                ficar vazio enquanto está em edição; sair dele arruma. */}
            <NumberInput
                size="sm" min={min} max={max} value={no.params?.[campo] ?? padrao}
                onChange={(texto, n) => mudarParam(noAberto, campo, texto === '' ? '' : (Number.isNaN(n) ? (no.params?.[campo] ?? padrao) : n))}
                onBlur={() => {
                    const v = no.params?.[campo];
                    if (v === '' || v === null || v === undefined || Number.isNaN(Number(v))) mudarParam(noAberto, campo, padrao);
                }}
            >
                <NumberInputField bg="white" />
            </NumberInput>
            {ajuda && <FormHelperText fontSize="xs">{ajuda}</FormHelperText>}
        </FormControl>
    );

    return (
        <Box ml={{ base: 0, md: '64px' }} pt="70px" px={{ base: 3, md: 6 }} pb={aba === 0 ? 24 : 8} minH="100vh" bg="gray.50">
            <Flex align="center" justify="center" gap={3} mb={1}>
                <Box as={FaPhoneAlt} boxSize={{ base: '20px', md: '26px' }} color={ACCENT} flexShrink={0} />
                <Heading as="h1" size={{ base: 'md', md: 'lg' }} color={ACCENT} textAlign="center">URA do telefone</Heading>
            </Flex>
            <Text fontSize="sm" color="gray.500" textAlign="center" mb={4}>
                Desenhe o caminho da ligação: arraste uma peça, ligue a tomada de uma caixa na entrada da outra.
            </Text>

            <VStack spacing={4} align="stretch" maxW="1400px" mx="auto">

                {erroCarga && (
                    <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box flex="1">
                            <AlertTitle fontSize="sm">A última atualização desta tela falhou.</AlertTitle>
                            <AlertDescription fontSize="xs">
                                {erroCarga} O que está na tela é do carregamento anterior — confira antes de publicar.
                            </AlertDescription>
                        </Box>
                        <Button {...alvo} variant="outline" leftIcon={<FaSyncAlt />} onClick={() => carregar()} flexShrink={0}>
                            Tentar de novo
                        </Button>
                    </Alert>
                )}

                {!status.pbx_ativo && (
                    <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box>
                            <AlertTitle fontSize="sm">O PABX próprio está desligado.</AlertTitle>
                            <AlertDescription fontSize="xs">Sem ele quem roteia a ligação é a operadora, e nada desta tela tem efeito.</AlertDescription>
                        </Box>
                    </Alert>
                )}

                {/* Interruptor + estado */}
                <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" px={{ base: 3, md: 5 }} py={3}>
                    <Flex align="center" justify="space-between" gap={4} wrap="wrap">
                        <HStack spacing={3}>
                            <Switch size="lg" colorScheme="orange" isChecked={!!cfg.ativo}
                                onChange={(e) => setCfg((c) => ({ ...c, ativo: e.target.checked }))} />
                            <Box>
                                <Text fontWeight="semibold" fontSize="sm">{cfg.ativo ? 'URA ligada' : 'URA desligada'}</Text>
                                <Text fontSize="xs" color="gray.500">
                                    {cfg.ativo ? 'Toda ligação passa pelo fluxo publicado.' : 'A ligação toca em todos os ramais, como antes.'}
                                </Text>
                            </Box>
                            <Button {...alvo} variant="outline" onClick={salvarConfig} isLoading={salvando}>aplicar</Button>
                        </HStack>
                        <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme={publicado ? 'green' : 'red'}>
                                {publicado ? `publicado v${publicado.versao}` : 'nada publicado'}
                            </Badge>
                            <Badge colorScheme={cfg.dentro_do_horario ? 'green' : 'gray'}>
                                {cfg.dentro_do_horario ? 'dentro do horário' : 'fora do horário'}
                            </Badge>
                            <Badge colorScheme={status.ramais_registrados?.length ? 'green' : 'gray'}>
                                {status.ramais_registrados?.length ? `ramais: ${status.ramais_registrados.join(', ')}` : 'nenhum ramal conectado'}
                            </Badge>
                            <Tooltip label={!status.bot_disponivel ? 'As peças "Atendente virtual" seguem direto pela saída “ninguém atendeu” enquanto ele estiver parado.' : ''} isDisabled={!!status.bot_disponivel}>
                                <Badge colorScheme={status.bot_disponivel ? 'green' : 'red'}>
                                    {status.bot_disponivel
                                        ? 'atendente virtual pronto'
                                        : `atendente virtual parado${MOTIVO_BOT[status.bot_motivo_parado] ? ` · ${MOTIVO_BOT[status.bot_motivo_parado]}` : ''}`}
                                </Badge>
                            </Tooltip>
                            <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} {...alvo} variant="ghost" onClick={() => carregar()} />
                        </HStack>
                    </Flex>
                </Box>

                {/* keepMounted: trocar de aba não pode zerar o filtro da lista. */}
                <Tabs variant="enclosed" colorScheme="orange" isLazy lazyBehavior="keepMounted" index={aba} onChange={setAba}>
                    <TabList overflowX="auto" overflowY="hidden">
                        <Tab fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap"><Box as={FaProjectDiagram} mr={2} /> Fluxo</Tab>
                        <Tab fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap"><Box as={FaChartBar} mr={2} /> Painel</Tab>
                        <Tab fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap"><Box as={FaPhoneVolume} mr={2} /> Ligações</Tab>
                        <Tab fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap"><Box as={FaSlidersH} mr={2} /> Ajustes</Tab>
                    </TabList>

                    <TabPanels>
                        {/* ---------------- editor ---------------- */}
                        <TabPanel px={0} pt={3}>
                            {/* Paleta + ações */}
                            <Flex gap={2} wrap="wrap" align="center" mb={3}>
                                <Wrap spacing={1} flex="1">
                                    {Object.entries(VISUAL).map(([tipo, v]) => (
                                        <WrapItem key={tipo}>
                                            <Button
                                                {...alvo} variant="outline" borderStyle="dashed"
                                                leftIcon={<Box as={v.icone} color={`${v.cor}.500`} />}
                                                onClick={() => adicionarPeca(tipo)}
                                            >
                                                {v.rotulo}
                                            </Button>
                                        </WrapItem>
                                    ))}
                                </Wrap>

                                <HStack spacing={2}>
                                    {arestaSelecionada && (
                                        <Button size="sm" colorScheme="red" variant="outline" leftIcon={<FaUnlink />} onClick={removerLigacao}>
                                            Remover ligação
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={reorganizar}>Arrumar</Button>
                                    <Menu>
                                        <MenuButton as={Button} size="sm" variant="outline">Começar de…</MenuButton>
                                        <MenuList>
                                            <MenuItem onClick={() => comecarDe('publicado')}>cópia do que está no ar</MenuItem>
                                            <MenuItem onClick={() => comecarDe('branco')}>fluxo em branco</MenuItem>
                                            <MenuItem onClick={() => comecarDe('antigo')}>fluxo padrão da TAGA</MenuItem>
                                        </MenuList>
                                    </Menu>
                                    <Button size="sm" variant="outline" leftIcon={<FaHistory />} onClick={abrirHistorico}>Histórico</Button>
                                </HStack>
                            </Flex>

                            {conflito && (
                                <Alert status="warning" borderRadius="md" mb={3} alignItems="flex-start" flexWrap="wrap">
                                    <AlertIcon />
                                    <Box flex="1" minW="200px">
                                        <AlertTitle fontSize="sm">O rascunho do servidor mudou e eu não salvei.</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            {conflito.mensagem} Seu desenho continua nesta tela: escolha ficar com o
                                            do servidor ou salvar o seu por cima.
                                        </AlertDescription>
                                    </Box>
                                    <HStack spacing={2} flexShrink={0} mt={{ base: 2, md: 0 }}>
                                        <Button
                                            {...alvo} variant="outline"
                                            onClick={() => {
                                                if (!window.confirm('Trazer o desenho do servidor? O que está nesta tela se perde.')) return;
                                                setConflito(null);
                                                setLoading(true);
                                                carregar({ forcarGrafo: true, forcarCfg: true });
                                            }}
                                        >
                                            Trazer o do servidor
                                        </Button>
                                        <Button {...alvo} colorScheme="orange" isLoading={salvando} onClick={() => salvar({ ignorarRevisao: true })}>
                                            Salvar o meu por cima
                                        </Button>
                                    </HStack>
                                </Alert>
                            )}

                            {/* Áudio que falta: o ensaio no 9998 fica mudo nesse passo */}
                            {faltamAudios > 0 && (
                                <Alert status="warning" borderRadius="md" mb={3} alignItems="flex-start">
                                    <AlertIcon />
                                    <Box flex="1">
                                        <AlertTitle fontSize="sm">{faltamAudios} áudio(s) ainda não foram gerados.</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            Ensaiar pelo 9998 agora deixa esses passos mudos. Publicar gera o que falta sozinho.
                                        </AlertDescription>
                                    </Box>
                                    <Button size="xs" colorScheme="orange" leftIcon={<FaVolumeUp />} isLoading={gerando}
                                        loadingText="gerando" onClick={() => gerarAudios(false)} flexShrink={0}>
                                        Gerar agora
                                    </Button>
                                </Alert>
                            )}

                            {/* Validação */}
                            {(validacao.erros?.length > 0 || validacao.avisos?.length > 0) && (
                                <Alert status={validacao.erros?.length ? 'error' : 'warning'} borderRadius="md" mb={3} alignItems="flex-start">
                                    <AlertIcon />
                                    <Box flex="1">
                                        <AlertTitle fontSize="sm">
                                            {validacao.erros?.length
                                                ? `${validacao.erros.length} pendência(s) impedem publicar`
                                                : `${validacao.avisos.length} aviso(s)`}
                                        </AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            <VStack align="stretch" spacing={0.5} mt={1}>
                                                {(validacao.erros || []).map((e, i) => <Text key={`e${i}`}>• {e}</Text>)}
                                                {(validacao.avisos || []).map((a, i) => <Text key={`a${i}`} color="gray.600">• {a}</Text>)}
                                            </VStack>
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            <Box position="relative" h={{ base: '58vh', md: '68vh' }} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                                <ReactFlow
                                    nodes={nodes}
                                    edges={edges}
                                    nodeTypes={TIPOS_NO}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    onConnect={aoConectar}
                                    onNodeDragStop={aoArrastar}
                                    onNodeClick={abrirNo}
                                    onSelectionChange={aoSelecionar}
                                    fitView
                                    fitViewOptions={{ padding: 0.2 }}
                                    minZoom={0.15}
                                    maxZoom={1.6}
                                    deleteKeyCode={null}
                                    proOptions={{ hideAttribution: true }}
                                >
                                    <Background color="#E2E8F0" gap={18} />
                                    {/* No celular a barra fixa de salvar come o canto de baixo:
                                        o zoom e o "caber na tela" — que são o que salva quem
                                        está com o dedo em cima de tomada pequena — ficavam
                                        embaixo dela. */}
                                    <Controls
                                        showInteractive={false}
                                        position={ehMobile ? 'top-left' : 'bottom-left'}
                                        style={ehMobile ? { transform: 'scale(1.4)', transformOrigin: 'top left' } : undefined}
                                    />
                                    {!ehMobile && <MiniMap pannable zoomable nodeColor={() => ACCENT} style={{ background: '#F7FAFC' }} />}
                                </ReactFlow>

                                {/* Rolar até a barra de cima para remover a seta não funciona no
                                    celular: o dedo em cima do canvas arrasta o desenho e a
                                    seleção se perde no caminho. */}
                                {arestaSelecionada && (
                                    <Button
                                        position="absolute" top={2} right={2} zIndex={5}
                                        size="sm" h={ehMobile ? '40px' : undefined}
                                        colorScheme="red" boxShadow="md"
                                        leftIcon={<FaUnlink />} onClick={removerLigacao}
                                    >
                                        Remover ligação
                                    </Button>
                                )}
                            </Box>

                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Tomada vermelha = saída sem destino. Clique numa seta para selecioná-la e remover.
                                Para ensaiar antes de publicar, disque <b>9998</b> de um ramal (o <b>9997</b> toca o que está no ar).
                            </Text>
                        </TabPanel>

                        {/* ---------------- painel ---------------- */}
                        <TabPanel px={0} pt={3}>
                            <PainelTab ativo={aba === 1} onVerLigacoes={(recorte) => { setPulo(recorte); setAba(2); }} />
                        </TabPanel>

                        {/* ---------------- ligações ---------------- */}
                        <TabPanel px={0} pt={3}>
                            <HistoricoTab pulo={pulo} aoLimparPulo={() => setPulo(null)} />
                        </TabPanel>

                        {/* ---------------- ajustes ---------------- */}
                        <TabPanel px={0} pt={3}>
                            <VStack spacing={4} align="stretch">
                                <Bloco titulo="Contatos que a URA fala" descricao="Entram nos áudios no lugar de {{whatsapp_falado}} e {{email_falado}}.">
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <FormControl>
                                            <FormLabel fontSize="sm">WhatsApp</FormLabel>
                                            <Input size="sm" bg="white" value={cfg.whatsapp || ''} onChange={(e) => setCfg((c) => ({ ...c, whatsapp: e.target.value }))} />
                                            <FormHelperText fontSize="xs">Falado: {status.whatsapp_falado || '—'}</FormHelperText>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="sm">E-mail de compras</FormLabel>
                                            <Input size="sm" bg="white" value={cfg.email_compras || ''} onChange={(e) => setCfg((c) => ({ ...c, email_compras: e.target.value }))} />
                                            <FormHelperText fontSize="xs">Falado: {status.email_falado || '—'}</FormHelperText>
                                        </FormControl>
                                    </SimpleGrid>
                                </Bloco>

                                <Bloco titulo="Horário comercial" descricao="É o que a peça “Condição · dentro do horário comercial” consulta.">
                                    <VStack align="stretch" spacing={3}>
                                        <HStack spacing={3}>
                                            <Switch colorScheme="orange" isChecked={!!cfg.horario_restrito} onChange={(e) => setCfg((c) => ({ ...c, horario_restrito: e.target.checked }))} />
                                            <Text fontSize="sm">{cfg.horario_restrito ? 'Tem horário definido' : 'Atende 24 horas'}</Text>
                                        </HStack>
                                        {cfg.horario_restrito && (
                                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={2}>
                                                {DIAS_SEMANA.map((d) => {
                                                    const dia = cfg.horario_dias[d.v] || { ativo: false, inicio: '08:00', fim: '18:00' };
                                                    const setDia = (patch) => setCfg((c) => {
                                                        const dias = normalizarGrade(c.horario_dias);
                                                        dias[d.v] = { ...dias[d.v], ...patch };
                                                        return { ...c, horario_dias: dias };
                                                    });
                                                    return (
                                                        <Flex key={d.v} align="center" gap={2} py={2} px={3} borderRadius="md" bg={dia.ativo ? 'white' : 'gray.50'} border="1px solid" borderColor="gray.200">
                                                            <Text w="38px" fontWeight="medium" fontSize="sm">{d.label}</Text>
                                                            <Switch size="sm" colorScheme="orange" isChecked={dia.ativo} onChange={(e) => setDia({ ativo: e.target.checked })} />
                                                            {dia.ativo && (
                                                                <>
                                                                    <Input type="time" size="sm" bg="white" flex="1" minW="90px" value={dia.inicio} onChange={(e) => setDia({ inicio: e.target.value })} />
                                                                    <Input type="time" size="sm" bg="white" flex="1" minW="90px" value={dia.fim} onChange={(e) => setDia({ fim: e.target.value })} />
                                                                </>
                                                            )}
                                                        </Flex>
                                                    );
                                                })}
                                            </SimpleGrid>
                                        )}
                                    </VStack>
                                </Bloco>

                                <Bloco titulo="Despedida de emergência" descricao="Toca quando o desenho não tem para onde mandar a ligação. É a rede embaixo do trapézio.">
                                    <FormControl>
                                        <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={1}>
                                            <FormLabel fontSize="sm" mb={0}>Texto</FormLabel>
                                            <LinhaAudio audio={audioDoTexto(cfg.textos?.['ura-sem-atendimento'])} tocando={tocando} onOuvir={ouvir} onParar={parar} />
                                        </Flex>
                                        <Textarea size="sm" bg="white" rows={2}
                                            value={cfg.textos?.['ura-sem-atendimento'] || ''}
                                            onChange={(e) => setCfg((c) => ({ ...c, textos: { ...c.textos, 'ura-sem-atendimento': e.target.value } }))} />
                                    </FormControl>
                                </Bloco>

                                <Bloco
                                    titulo="Voz"
                                    descricao="Vale para todos os áudios. Mudou a voz ou a velocidade, é preciso gerar tudo de novo."
                                    acao={<Button size="sm" colorScheme="orange" variant="outline" leftIcon={<FaVolumeUp />} isLoading={gerando} loadingText="gerando" onClick={() => gerarAudios(true)}>Regerar todos</Button>}
                                >
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <FormControl>
                                            <FormLabel fontSize="sm">Locutor</FormLabel>
                                            <Select size="sm" bg="white" value={cfg.tts_voz || 'onyx'} onChange={(e) => setCfg((c) => ({ ...c, tts_voz: e.target.value }))}>
                                                {['onyx', 'ash', 'alloy', 'echo', 'sage', 'coral', 'nova', 'shimmer'].map((v) => <option key={v} value={v}>{v}</option>)}
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="sm">Velocidade</FormLabel>
                                            <NumberInput size="sm" min={0.5} max={2} step={0.02} precision={2} value={cfg.tts_velocidade}
                                                onChange={(texto, n) => setCfg((c) => ({ ...c, tts_velocidade: texto === '' ? '' : (Number.isNaN(n) ? c.tts_velocidade : n) }))}
                                                onBlur={() => setCfg((c) => (c.tts_velocidade === '' || Number.isNaN(Number(c.tts_velocidade)) ? { ...c, tts_velocidade: 1.12 } : c))}>
                                                <NumberInputField bg="white" />
                                            </NumberInput>
                                        </FormControl>
                                    </SimpleGrid>
                                    <FormControl mt={4}>
                                        <FormLabel fontSize="sm">Instrução de locução</FormLabel>
                                        <Textarea size="sm" bg="white" rows={3} value={cfg.tts_instrucoes || ''} onChange={(e) => setCfg((c) => ({ ...c, tts_instrucoes: e.target.value }))} />
                                    </FormControl>
                                </Bloco>

                                <Bloco
                                    titulo="Todos os áudios"
                                    descricao="O nome vem do conteúdo: texto igual em duas caixas usa o mesmo arquivo."
                                    acao={faltamAudios > 0 && (
                                        <Button size="sm" colorScheme="orange" leftIcon={<FaVolumeUp />} isLoading={gerando}
                                            loadingText="gerando" onClick={() => gerarAudios(false)}>
                                            Gerar os {faltamAudios} que faltam
                                        </Button>
                                    )}
                                >
                                    <VStack align="stretch" spacing={2}>
                                        {audios.map((a) => (
                                            <Flex key={a.nome} gap={3} align="flex-start" py={2} borderBottom="1px solid" borderColor="gray.100" wrap="wrap">
                                                <Box flex="1" minW="200px">
                                                    <Text fontSize="xs" color="gray.500" noOfLines={2}>{a.texto}</Text>
                                                    {a.atualizado_em && <Text fontSize="xs" color="gray.400">gerado em {a.atualizado_em}</Text>}
                                                </Box>
                                                <LinhaAudio audio={a} tocando={tocando} onOuvir={ouvir} onParar={parar} />
                                            </Flex>
                                        ))}
                                    </VStack>
                                </Bloco>

                                <HStack alignSelf="flex-start" spacing={3}>
                                    <Button colorScheme="orange" leftIcon={<FaSave />} onClick={salvarConfig} isLoading={salvando}>
                                        Salvar ajustes
                                    </Button>
                                    {cfgNaoSalvo && <Badge colorScheme="orange">ajustes não salvos</Badge>}
                                </HStack>
                            </VStack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </VStack>

            {/* Editor da caixa */}
            <Drawer isOpen={drawer.isOpen} placement={ehMobile ? 'bottom' : 'right'} onClose={fecharDrawer} size={ehMobile ? 'full' : 'md'}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px" fontSize="md">
                        {no ? ((VISUAL[no.tipo] || {}).rotulo || no.tipo) : ''}
                    </DrawerHeader>
                    <DrawerBody py={5}>
                        {no && (
                            <VStack align="stretch" spacing={4}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Nome da caixa</FormLabel>
                                    <Input size="sm" bg="white" value={no.nome || ''} onChange={(e) => mudarNo(noAberto, { nome: e.target.value })} />
                                    <FormHelperText fontSize="xs">Só para você se achar no desenho.</FormHelperText>
                                </FormControl>

                                {no.tipo === 'audio' && campoTexto('texto', 'O que o cliente ouve', 'Pode usar {{whatsapp_falado}} e {{email_falado}}.', 4)}

                                {no.tipo === 'encerrar' && campoTexto('texto', 'Despedida (opcional)', 'Vazio = desliga sem falar nada.', 3)}

                                {no.tipo === 'menu' && (
                                    <>
                                        {campoTexto('texto', 'Saudação e opções', 'O dígito interrompe o áudio.', 4)}
                                        {campoTexto('texto_curto', 'Repetição (versão curta)', 'Toca a partir da segunda vez. Vazio = repete o longo.', 3)}
                                        <SimpleGrid columns={2} spacing={3}>
                                            {campoNumero('espera_seg', 'Espera o dígito', 'segundos', 3, 30, 6)}
                                            {campoNumero('tentativas', 'Repete até', 'vezes', 1, 5, 2)}
                                        </SimpleGrid>
                                        <Divider />
                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" mb={2}>Dígitos deste menu</Text>
                                            <Wrap spacing={2} mb={2}>
                                                {Object.keys(no.saidas || {}).filter((e) => e.startsWith('digito_')).sort().map((e) => (
                                                    <WrapItem key={e}>
                                                        <Tag size="md" colorScheme={no.saidas[e] ? 'orange' : 'red'} borderRadius="full">
                                                            <TagLabel>{e.slice(7)} {no.saidas[e] ? `→ ${grafo.nos[no.saidas[e]]?.nome || '?'}` : '(sem destino)'}</TagLabel>
                                                            <IconButton aria-label="tirar" icon={<FaTrash />} size={ehMobile ? 'sm' : 'xs'} variant="ghost" ml={1} onClick={() => removerSaida(noAberto, e)} />
                                                        </Tag>
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                            <HStack spacing={1} flexWrap="wrap">
                                                {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
                                                    .filter((d) => !(no.saidas || {})[`digito_${d}`])
                                                    .map((d) => (
                                                        <Button key={d} {...alvo} variant="outline" leftIcon={<FaPlus />} onClick={() => mudarSaida(noAberto, `digito_${d}`, '')}>
                                                            {d}
                                                        </Button>
                                                    ))}
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500" mt={2}>
                                                O dígito aparece como tomada na caixa: arraste dela até a caixa de destino —
                                                ou escolha o destino na lista <b>Saídas</b>, logo abaixo.
                                            </Text>
                                        </Box>
                                    </>
                                )}

                                {no.tipo === 'ramais' && campoNumero('ring_seg', 'Toca por', 'segundos em quem estiver com o softphone aberto', 5, 60, 12)}

                                {no.tipo === 'ramal' && (
                                    <>
                                        <FormControl>
                                            <FormLabel fontSize="sm">De quem é o ramal</FormLabel>
                                            <Select size="sm" bg="white" value={no.params?.user_id || ''} onChange={(e) => mudarParam(noAberto, 'user_id', e.target.value ? Number(e.target.value) : null)}>
                                                <option value="">escolha…</option>
                                                {semBloqueados(ref.usuarios || [], no.params?.user_id).map((u) => <option key={u.id} value={u.id}>{u.nome} — {u.ramal}</option>)}
                                            </Select>
                                            <FormHelperText fontSize="xs">Ramal desconectado na hora da ligação segue direto pela saída “ninguém atendeu”.</FormHelperText>
                                        </FormControl>
                                        {campoNumero('ring_seg', 'Toca por', 'segundos', 5, 60, 12)}
                                    </>
                                )}

                                {no.tipo === 'gerente' && campoNumero('ring_seg', 'Toca por', 'segundos no ramal do gerente de contas do cliente', 5, 60, 20)}

                                {no.tipo === 'bot' && (
                                    <FormControl>
                                        <FormLabel fontSize="sm">Com que chapéu ele atende</FormLabel>
                                        <Select size="sm" bg="white" value={no.params?.motivo || 'vendas'} onChange={(e) => mudarParam(noAberto, 'motivo', e.target.value)}>
                                            {(ref.motivos || []).map((m) => <option key={m.valor} value={m.valor}>{m.label}</option>)}
                                        </Select>
                                        <FormHelperText fontSize="xs">
                                            O atendente virtual tem horário e teto de custo próprios (tela do bot de ligação).
                                            Parado, a ligação segue direto pela saída “ninguém atendeu”.
                                        </FormHelperText>
                                    </FormControl>
                                )}

                                {no.tipo === 'condicao' && (
                                    <FormControl>
                                        <FormLabel fontSize="sm">Perguntar se</FormLabel>
                                        <Select size="sm" bg="white" value={no.params?.condicao || ''} onChange={(e) => mudarParam(noAberto, 'condicao', e.target.value)}>
                                            <option value="">escolha…</option>
                                            {(ref.condicoes || []).map((c) => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                                        </Select>
                                    </FormControl>
                                )}

                                {no.tipo === 'transbordo' && (
                                    <Text fontSize="sm" color="gray.600">
                                        Devolve a ligação sem atender: o “transbordo em caso de falha” da operadora assume.
                                        Só funciona <b>antes</b> de tocar qualquer áudio — depois disso a ligação já está atendida.
                                    </Text>
                                )}

                                <Divider />

                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={1}>Saídas</Text>
                                    {/* Arrastar da tomada era o ÚNICO jeito de ligar duas caixas —
                                        e no celular a tomada tem o tamanho de um grão de arroz.
                                        Aqui a ligação se faz escolhendo o destino na lista. */}
                                    <Text fontSize="xs" color="gray.500" mb={2}>Para onde a ligação vai em cada caso.</Text>
                                    <VStack align="stretch" spacing={2}>
                                        {saidasDoNo(no).map((e) => (
                                            <Box key={e} pb={2} borderBottom="1px solid" borderColor="gray.100">
                                                <Flex justify="space-between" align="center" gap={2} mb={1}>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.700">{rotuloSaida(e)}</Text>
                                                    {!no.saidas?.[e] && (saidaOpcional(no.tipo, e)
                                                        ? <Text fontSize="xs" color="gray.500">opcional · sem destino não transfere</Text>
                                                        : <Text fontSize="xs" color="red.500">sem destino</Text>)}
                                                </Flex>
                                                <HStack spacing={2}>
                                                    <Select
                                                        size="sm" bg="white" flex="1"
                                                        value={no.saidas?.[e] || ''}
                                                        onChange={(ev) => mudarSaida(noAberto, e, ev.target.value)}
                                                    >
                                                        <option value="">sem destino</option>
                                                        {destinos.map(([id, n]) => (
                                                            <option key={id} value={id}>{n.nome || (VISUAL[n.tipo] || {}).rotulo || id}</option>
                                                        ))}
                                                    </Select>
                                                    {no.saidas?.[e] && (
                                                        <Tooltip label={e.startsWith('digito_') ? 'tirar este dígito do menu' : 'desligar esta saída'}>
                                                            <IconButton
                                                                aria-label="desligar" icon={<FaUnlink />}
                                                                size="sm" variant="ghost" colorScheme="red"
                                                                onClick={() => removerSaida(noAberto, e)}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </HStack>
                                            </Box>
                                        ))}
                                        {!saidasDoNo(no).length && <Text fontSize="xs" color="gray.500">Esta peça termina a ligação.</Text>}
                                    </VStack>
                                </Box>

                                <Divider />

                                <HStack>
                                    <Button size="sm" variant="outline" leftIcon={<FaFlag />} isDisabled={grafo.inicio === noAberto}
                                        onClick={() => setGrafo((g) => ({ ...g, inicio: noAberto }))}>
                                        {grafo.inicio === noAberto ? 'É o começo' : 'Começar por aqui'}
                                    </Button>
                                    <Button size="sm" colorScheme="red" variant="outline" leftIcon={<FaTrash />} onClick={() => removerNo(noAberto)}>
                                        Remover
                                    </Button>
                                </HStack>
                            </VStack>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Histórico */}
            <Modal isOpen={historico.isOpen} onClose={historico.onClose} size="lg" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">Versões publicadas</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        <VStack align="stretch" spacing={2}>
                            {!versoes.length && <Text fontSize="sm" color="gray.500">Nenhuma versão ainda.</Text>}
                            {versoes.map((v) => (
                                <Flex key={v.id} justify="space-between" align="center" gap={3} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md" wrap="wrap">
                                    <Box>
                                        <HStack spacing={2}>
                                            <Text fontWeight="semibold" fontSize="sm">v{v.versao}</Text>
                                            {v.status === 'publicado' && <Badge colorScheme="green">no ar</Badge>}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">{v.resumo}</Text>
                                        {v.publicado_em && <Text fontSize="xs" color="gray.400">publicado em {v.publicado_em}</Text>}
                                    </Box>
                                    {v.status !== 'publicado' && (
                                        <Button size="xs" leftIcon={<FaUndo />} variant="outline" onClick={() => voltarPara(v.id, v.versao)}>
                                            voltar para esta
                                        </Button>
                                    )}
                                </Flex>
                            ))}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" onClick={historico.onClose}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Salvar / publicar — só na aba do desenho: nas outras não há o que
                salvar, e a barra ainda comia 56 px e cobria os controles do canvas. */}
            {aba === 0 && (
            <Flex
                position="fixed" bottom={0} left={{ base: 0, md: '64px' }} right={0}
                bg="white" borderTop="1px solid" borderColor="gray.200"
                px={{ base: 3, md: 6 }} py={3} gap={2} align="center" justify="flex-end"
                boxShadow="0 -2px 8px rgba(0,0,0,0.04)" zIndex={1400} flexWrap="wrap"
            >
                {gerando && <HStack spacing={2}><Spinner size="sm" color={ACCENT} /><Text fontSize="xs" color="gray.500">gerando áudios…</Text></HStack>}
                <Text fontSize="xs" color="gray.500" display={{ base: 'none', lg: 'block' }} mr="auto">
                    {naoSalvo
                        ? 'O desenho tem mudanças que ainda não foram salvas.'
                        : (rascunhoInfo.igual_ao_publicado
                            ? 'O rascunho está igual ao que está no ar.'
                            : 'O rascunho está salvo, mas ainda não está no ar.')}
                </Text>
                {/* No celular a frase acima não cabe; o aviso não pode sumir junto. */}
                {naoSalvo && (
                    <Badge colorScheme="orange" display={{ base: 'inline-flex', lg: 'none' }} mr="auto">não salvo</Badge>
                )}
                {/* Enquanto o Publicar roda ele salva o rascunho por dentro: um
                    Salvar ou Validar no meio disso são dois PUT concorrentes. */}
                <Button
                    size="sm" h={ehMobile ? '40px' : undefined} variant="outline" leftIcon={<FaCheckCircle />}
                    onClick={validar} isDisabled={publicando || salvando}
                >
                    Validar
                </Button>
                <Button
                    size="sm" h={ehMobile ? '40px' : undefined}
                    variant={naoSalvo ? 'solid' : 'outline'} colorScheme={naoSalvo ? 'gray' : undefined}
                    leftIcon={<FaSave />} onClick={() => salvar()} isLoading={salvando} isDisabled={publicando}
                >
                    Salvar rascunho
                </Button>
                <Tooltip label={
                    naoSalvo
                        ? 'Salva o desenho e põe no ar'
                        : (validacao.erros?.length ? 'Resolva as pendências primeiro' : 'Passa a valer na próxima ligação')
                }>
                    <Button
                        size="sm" h={ehMobile ? '40px' : undefined} colorScheme="orange" leftIcon={<FaUpload />}
                        isLoading={publicando} loadingText="publicando"
                        // Com mudança na tela a validação exibida é a do desenho
                        // anterior: travar por ela esconderia a correção que
                        // acabou de ser feita. Quem decide é o servidor, no
                        // publicar — que agora salva antes.
                        isDisabled={salvando || (!naoSalvo && validacao.erros?.length > 0)}
                        onClick={publicar}
                    >
                        Publicar
                    </Button>
                </Tooltip>
            </Flex>
            )}
        </Box>
    );
};

const Ura = () => (
    <ReactFlowProvider>
        <UraInterno />
    </ReactFlowProvider>
);

export default Ura;
